if (!customElements.get("klaviyo-bis-panel")) {
  customElements.define(
    "klaviyo-bis-panel",
    class KlaviyoBISPanel extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        // Move the panel to <body> so it escapes the section's stacking context
        // and sits above the sticky header (z-index: 1001 vs header's sticky context)
        document.body.appendChild(this);

        const bisId = this.dataset.bisId;
        const overlay = this.querySelector(".klaviyo-bis__overlay");
        const panel = this.querySelector(".klaviyo-bis__panel");
        const closeBtn = this.querySelector(".klaviyo-bis__close");
        const triggers = document.querySelectorAll(
          `[data-bis-trigger="${bisId}"]`,
        );

        this._companyId = this.dataset.companyId || "WPfH8e";
        this._sectionId = this.dataset.sectionId;
        this._variantId = null;
        this._variantName = null;
        this._isSubmitting = false;
        this._isClosing = false;

        this._form = this.querySelector("[data-bis-form]");
        this._emailInput = this.querySelector(".klaviyo-bis__email");
        this._errorEl = this.querySelector("[data-bis-error]");
        this._formWrap = this.querySelector("[data-bis-form-wrap]");
        this._successEl = this.querySelector("[data-bis-success]");
        this._variantNameEl = this.querySelector("[data-bis-variant-name]");
        this._submitBtn = this.querySelector(".klaviyo-bis__submit");
        this._consentCheckbox = this.querySelector("[data-bis-consent]");
        this._productDetails = this.querySelector(
          ".klaviyo-bis__product-details",
        );
        this._header = this.querySelector(".klaviyo-bis__header");

        this._emailInput.addEventListener("input", (e) => {
          if (!this._isSubmitting) {
            this._submitBtn.disabled = !e.target.value.trim();
          }
        });

        const open = (variantId, variantName) => {
          this._variantId = variantId;
          this._variantName = variantName || "";

          panel.classList.remove("is-closing");
          overlay.classList.remove("is-closing");

          // Update content while panel is still off-screen
          this._reset();
          if (this._variantNameEl)
            this._variantNameEl.textContent = this._formatVariantName(variantName);

          // Show overlay first (display: block), then fade in on next frame
          overlay.hidden = false;
          panel.hidden = false;
          overlay.style.display = "block";
          requestAnimationFrame(() => {
            overlay.classList.add("is-open");
            panel.classList.add("is-open");
          });

          if (typeof window.lockPdpSidebarPageScroll === "function") {
            window.lockPdpSidebarPageScroll();
          } else {
            document.body.style.overflow = "hidden";
          }

          // Focus email after transition
          requestAnimationFrame(() => {
            setTimeout(() => this._emailInput?.focus(), 50);
          });
        };

        const close = () => {
          // Prevent multiple close calls
          if (this._isClosing) return;
          this._isClosing = true;

          panel.classList.add("is-closing");
          overlay.classList.remove("is-open");
          overlay.classList.add("is-closing");

          const unlockScroll = () => {
            if (typeof window.unlockPdpSidebarPageScroll === "function") {
              window.unlockPdpSidebarPageScroll();
            } else {
              document.body.style.overflow = "";
            }
          };

          // Reset form after panel transition completes
          const onTransitionEnd = (e) => {
            // Only trigger on panel transform, not child elements
            if (e.target === panel && e.propertyName === "transform") {
              panel.removeEventListener("transitionend", onTransitionEnd);
              panel.classList.remove("is-open", "is-closing");
              overlay.classList.remove("is-closing");
              overlay.hidden = true;
              panel.hidden = true;
              overlay.style.display = "none";
              unlockScroll();
              this._reset();
              // Closing the panel — submitted or not — always hands control
              // back to Add to Cart and requires a fresh size pick.
              this._resetTriggersForNextSize();
              this._isClosing = false;
            }
          };

          panel.addEventListener("transitionend", onTransitionEnd);

          // Fallback timeout in case transitionend doesn't fire
          setTimeout(() => {
            panel.removeEventListener("transitionend", onTransitionEnd);
            if (this._isClosing) {
              panel.classList.remove("is-open", "is-closing");
              overlay.classList.remove("is-closing");
              overlay.hidden = true;
              panel.hidden = true;
              overlay.style.display = "none";
              unlockScroll();
              this._reset();
              this._resetTriggersForNextSize();
              this._isClosing = false;
            }
          }, 500);
        };

        this._triggers = triggers;

        // Bind triggers
        triggers.forEach((btn) => {
          btn.addEventListener("mouseenter", () => {
            if (btn.classList.contains("no-size__selected")) {
              btn.classList.add("bis--hover-select-size");
            }
          });
          btn.addEventListener("mouseleave", () => {
            btn.classList.remove("bis--hover-select-size");
          });

          btn.addEventListener("click", () => {
            if (btn.classList.contains("no-size__selected")) {
              const panelReveal = typeof window.ScrollTrigger !== 'undefined'
                ? window.ScrollTrigger.getById('pdp-panel-reveal')
                : null;
              const isCollapsed = panelReveal ? panelReveal.progress < 0.5 : true;
              const isMobile = window.innerWidth <= 1023;

              const sectionId = btn.closest("[data-section-id]")?.dataset.sectionId;

              if (isMobile && isCollapsed && typeof window.sizePeekOpen === 'function') {
                const sizePopupEl = document.querySelector('.size-popup-wrapper');
                const isSizePopupOpen = sizePopupEl && sizePopupEl.classList.contains('is-open');
                if (!isSizePopupOpen) {
                  window.sizePeekOpen();
                  return;
                }

                // Popup already open — show warning inside the fieldset so it's visible to the user
                const fieldset = sizePopupEl.querySelector('.product-form__input--pill');
                const target = fieldset || sizePopupEl;
                let popupWarning = target.querySelector('.size-popup-warning');
                if (!popupWarning) {
                  const pageWarning = document.getElementById(`SelectSizeWarning-${sectionId}`);
                  popupWarning = document.createElement('p');
                  popupWarning.className = 'product-form__select-size-warning body-text size-popup-warning';
                  popupWarning.setAttribute('role', 'alert');
                  popupWarning.textContent = pageWarning ? pageWarning.textContent.trim() : 'Please select a size';
                  target.appendChild(popupWarning);
                }
                popupWarning.hidden = false;
                btn.classList.add("bis--hover-select-size", "bis--size-prompt");
                return;
              }

              const warning = document.getElementById(`SelectSizeWarning-${sectionId}`);
              if (warning) warning.hidden = false;
              btn.classList.add("bis--hover-select-size", "bis--size-prompt");
              return;
            }
            const variantId = btn.dataset.variantId;
            const variantName = btn.dataset.variantName;

            // Close the size-peek popup (mobile) the same way a successful add to
            // cart does — the size has been "used" for this notify request.
            const sizePopupEl = document.querySelector(".size-popup-wrapper");
            if (sizePopupEl && sizePopupEl.classList.contains("is-open") && typeof window.sizePeekClose === "function") {
              window.sizePeekClose();
            }

            open(variantId, variantName);
          });
        });

        closeBtn.addEventListener("click", close);
        overlay.addEventListener("click", close);

        // Close on Keep Shopping button and any other [data-bis-close] elements
        this.querySelectorAll("[data-bis-close]").forEach((el) => {
          el.addEventListener("click", close);
        });

        document.addEventListener("keydown", (e) => {
          if (e.key === "Escape" && panel.classList.contains("is-open"))
            close();
        });

        // Form submission
        this._form?.addEventListener("submit", this._onSubmit.bind(this));

        // Store close function for external access
        this.close = close;
        this.open = open;
      }

      _reset() {
        if (this._emailInput) this._emailInput.value = "";
        if (this._errorEl) this._errorEl.hidden = true;
        if (this._formWrap) this._formWrap.hidden = false;
        if (this._successEl) this._successEl.hidden = true;
        if (this._productDetails) this._productDetails.hidden = false;
        if (this._submitBtn) {
          this._submitBtn.disabled = true;
          this._submitBtn.classList.remove("loading");
        }
        if (this._consentCheckbox) {
          this._consentCheckbox.checked = false;
        }
        this._isSubmitting = false;
      }

      _onSubmit(e) {
        e.preventDefault();

        // Prevent duplicate submissions
        if (this._isSubmitting) return;

        const email = this._emailInput?.value?.trim();
        if (!this._validateEmail(email)) {
          if (this._errorEl) this._errorEl.hidden = false;
          this._submitBtn.disabled = true;
          this._emailInput?.focus();
          return;
        }
        if (this._errorEl) this._errorEl.hidden = true;
        this._submit(email);
      }

      _validateEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      }

      _formatVariantName(variantName) {
        if (!variantName || variantName === "Default Title") return "One Size";

        const activeSystem = this._getActiveSizeSystem();
        const sizeSystems = ["uk", "us", "eu"];
        const parts = variantName.split(" / ").map((part) => part.trim()).filter(Boolean);
        if (parts.length > 1) {
          const sizeParts = parts.filter((part) => {
            const prefix = part.split(/\s+/)[0]?.toLowerCase();
            return sizeSystems.includes(prefix);
          });
          const activeSize = sizeParts.find((part) => {
            return part.split(/\s+/)[0]?.toLowerCase() === activeSystem;
          });
          if (activeSize) return activeSize.split(/\s+/).slice(1).join(" ");
          if (sizeParts.length) return sizeParts[0].split(/\s+/).slice(1).join(" ");
        }

        return variantName;
      }

      _getActiveSizeSystem() {
        const activeTab = document.querySelector(".size-tab.active");
        return sessionStorage.getItem("preferred-size-system") || activeTab?.dataset.sizeSystem || "eu";
      }

      _submit(email) {
        if (!this._variantId || !email) return;

        // Prevent multiple simultaneous submissions
        if (this._isSubmitting) return;
        this._isSubmitting = true;

        this._submitBtn.disabled = true;
        this._submitBtn.classList.add("loading");

        const payload = {
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              channels: ["EMAIL"],
              profile: {
                data: {
                  type: "profile",
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: "catalog-variant",
                  id: `$shopify:::$default:::${this._variantId}`,
                },
              },
            },
          },
        };

        fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this._companyId}`,
          {
            method: "POST",
            headers: {
              "content-type": "application/json",
              revision: "2023-12-15",
            },
            body: JSON.stringify(payload),
          },
        )
          .then((res) => {
            if (res.ok || res.status === 202) {
              this._isSubmitting = false;
              this._showSuccess(email);
            } else {
              throw new Error("BIS failed");
            }
          })
          .catch(() => {
            this._isSubmitting = false;
            if (this._errorEl) {
              this._errorEl.textContent =
                window.bisStrings?.error ||
                "Something went wrong. Please try again.";
              this._errorEl.hidden = false;
            }
            if (this._submitBtn) {
              this._submitBtn.disabled = false;
              this._submitBtn.classList.remove("loading");
            }
          });
      }

      _showSuccess(email) {
        const successBody = this.querySelector("[data-bis-success-body]");
        if (successBody) {
          const tpl =
            window.bisStrings?.successBody ||
            "You'll receive an email at {email} when this size is back in stock.";
          const safeEmail = email.replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
          successBody.innerHTML = tpl.replace("{email}", `<strong class="klaviyo-bis__success-email">${safeEmail}</strong>`);
        }
        if (this._formWrap) this._formWrap.hidden = true;
        if (this._successEl) this._successEl.hidden = false;
        if (this._productDetails) this._productDetails.hidden = true;

        // Size reselection is required before the trigger can be used again —
        // handled uniformly in close() for every dismissal, submitted or not.
      }

      _resetTriggersForNextSize() {
        // A product with no real size choice — either no <variant-selects>
        // pill fieldset at all (default-variant-only product), or a single-size
        // fieldset (data-single-size, only ever one value) — has nothing else
        // to pick. Switching back to Add to Cart would just show a button that
        // immediately fails again, so keep Notify Me up instead.
        const sizeFieldset = document.querySelector('.product-form__input--pill');
        const hasSelectableSizeOptions = !!sizeFieldset && !sizeFieldset.hasAttribute('data-single-size');
        if (!hasSelectableSizeOptions) return;

        const productInfoEl = document.querySelector("product-info");

        // Every size sold out (product totally unavailable) — picking a
        // different size wouldn't help, so Notify Me should stay up instead
        // of handing back an Add to Cart button that will immediately fail.
        const isProductTotallySoldOut = productInfoEl?.dataset.productAvailable === 'false';

        this._triggers?.forEach((btn) => {
          // Always require a fresh size pick before the trigger reopens the
          // panel — submitted or not, the size that was just used shouldn't
          // silently carry over to the next Notify Me click.
          btn.classList.add("no-size__selected");
          btn.classList.remove("bis--hover-select-size", "bis--size-prompt");
          if (!isProductTotallySoldOut) {
            // The trigger was shown (display override cleared) because a sold-out
            // variant was selected — hide it again now that the size has been
            // "used", otherwise it sits there stale after the panel closes instead
            // of handing control back to the Add to Cart button below.
            btn.style.display = "none";
          }
          // Totally sold-out product — leave the trigger visible; there's no
          // working Add to Cart state to hand back to.
        });

        const scope = productInfoEl || document;
        const sizeInputs = scope.querySelectorAll('.product-form__input--pill input[type="radio"]');
        sizeInputs.forEach((input) => {
          input.checked = false;
          input.removeAttribute("checked");
        });

        const popupSizeInputs = document.querySelectorAll('.size-popup-wrapper .product-form__input--pill input[type="radio"]');
        popupSizeInputs.forEach((input) => {
          input.checked = false;
          input.removeAttribute("checked");
        });

        // Totally sold out — the Add to Cart button must stay hidden, so skip
        // resetting it back into view.
        if (isProductTotallySoldOut) return;

        // The Add to Cart button shares the same fieldset — keep it in sync so it
        // also requires a fresh size pick instead of reflecting a now-unchecked radio.
        // Queried document-wide (not just `scope`) since the mobile peek bar's
        // button lives outside <product-info> — every .product-form__submit match
        // (real button + peek twin) needs the same reset, not just the first one.
        // Excludes .pdp-bis__trigger — the Notify Me buttons share the
        // .product-form__submit class too, and this reset (display/disabled/
        // label) is only meaningful for the actual Add to Cart buttons.
        document.querySelectorAll(".product-form__submit:not(.pdp-bis__trigger)").forEach((submitBtn) => {
          submitBtn.classList.add("no-size__selected");
          // Clear any leftover "Select size" prompt state from before the notify
          // flow started — otherwise it survives the reset (no-size__selected
          // alone isn't enough to trigger it, but combined with this leftover
          // class it shows "Select size" instead of the plain "Add to Cart" label).
          submitBtn.classList.remove("atc--hover-select-size");
          submitBtn.removeAttribute("disabled");
          submitBtn.style.removeProperty("display");

          // A sold-out size selection swaps this label to "Sold Out" — put it
          // back now that the button no longer reflects that size.
          const submitBtnText = submitBtn.querySelector(".atc-text");
          if (submitBtnText) submitBtnText.textContent = window.variantStrings.addToCart;
        });
      }
    },
  );
}
